import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-accent-gradient text-white shadow-card hover:shadow-card-hover hover:brightness-[1.04] focus-visible:shadow-focus-accent active:brightness-95',
  secondary:
    'bg-white text-kalkuel-primary border border-kalkuel-border shadow-sm hover:border-kalkuel-accent-strong hover:text-kalkuel-accent-strong hover:shadow-card focus-visible:shadow-focus-accent',
  ghost:
    'text-kalkuel-primary hover:bg-kalkuel-accent-soft hover:text-kalkuel-accent-strong focus-visible:shadow-focus-accent',
  danger:
    'bg-red-600 text-white shadow-card hover:bg-red-700 hover:shadow-card-hover focus-visible:ring-2 focus-visible:ring-red-500 active:brightness-95',
};

const SIZE: Record<Size, string> = {
  sm: 'h-7 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-6 text-sm',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-field font-medium',
        'transition-all duration-150 will-change-transform',
        'hover:-translate-y-px active:translate-y-0 active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-offset-0',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:translate-y-0',
        VARIANT[variant],
        SIZE[size],
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}
